/**
 * Why a translator would not start, as a code rather than a sentence.
 *
 * The adapter knows what went wrong; it does not know what language the reader
 * has chosen. Passing a code keeps those two decisions apart — otherwise every
 * message would be frozen in whatever language the adapter was written in.
 */
export interface TranslatorFailure {
  code:
    | 'offline'
    | 'no_credit'
    | 'rate_limited'
    | 'unconfigured'
    | 'unreachable'
    | 'session_expired'
    | 'room_closed'
    | 'bad_offer'
    | 'provider_refused'
    | 'unknown';
  /** Present for `provider_refused`, where the number is the only detail. */
  status?: number;
}

export class TranslatorError extends Error {
  readonly failure: TranslatorFailure;

  constructor(failure: TranslatorFailure) {
    super(failure.code);
    this.failure = failure;
  }
}

/** Codes our server sends, mapped onto the ones the UI has words for. */
const FROM_API: Record<string, TranslatorFailure['code']> = {
  provider_unconfigured: 'unconfigured',
  provider_no_credit: 'no_credit',
  provider_rate_limited: 'rate_limited',
  provider_unreachable: 'unreachable',
  provider_error: 'unreachable',
  session_expired: 'session_expired',
  no_session: 'session_expired',
  unauthorized: 'room_closed',
  bad_sdp: 'bad_offer',
};

export function failureFromApi(code: string, status: number): TranslatorFailure {
  const mapped = FROM_API[code];
  if (mapped) return { code: mapped };
  return status >= 400 ? { code: 'provider_refused', status } : { code: 'unknown' };
}
